/**
 * La franja de entrega de la corrida, y la resolución de lo que el usuario escribe.
 *
 * Por qué existe: junto con la atención por parada, la franja es la **otra mitad** de
 * la ecuación del tiempo, y hasta ahora era una constante que nadie podía tocar. Con
 * 15 minutos de atención, 36 paradas agotan una jornada de 540 minutos sin moverse un
 * metro; sumado el viaje, el techo real ronda las 20-25 paradas por grupo. Los pedidos
 * que quedan afuera con `shift_full` salen por falta de MINUTOS, no de capacidad — y
 * el número de minutos no estaba en el maestro ni en la pantalla, estaba quemado.
 *
 * El maestro tampoco lo tiene: `subRoute.itinerary.datetimeStart`/`datetimeEnd` llevan
 * el rango de **vigencia** del itinerario, no una hora del día (ver `subroute-fleet.ts`).
 * Así que la franja es una decisión de la corrida y el lugar correcto para declararla
 * es la pantalla.
 */

/** La franja que la pantalla asumía cuando era una constante. Sigue siendo el default. */
export const DEFAULT_SHIFT: Readonly<{ start: string; end: string }> = {
  start: '08:00',
  end: '17:00',
};

/**
 * Piso de duración, en minutos.
 *
 * Una franja de treinta minutos es sintácticamente impecable y operativamente absurda:
 * no entra ninguna parada, la corrida entera vuelve sin asignar y el usuario no tiene
 * forma de saber que la causa fue el campo que acaba de escribir. Es el mismo criterio
 * por el que la atención por parada tiene un mínimo.
 */
export const MIN_SHIFT_MINUTES = 120;

/** Minutos desde medianoche de un `HH:MM`, o `null` si no es una hora. */
function toMinutes(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Minutos que dura la franja. */
export function shiftMinutes(shift: { start: string; end: string }): number {
  const from = toMinutes(shift.start);
  const to = toMinutes(shift.end);
  if (from === null || to === null) return 0;
  return Math.max(0, to - from);
}

/**
 * La franja a usar en la corrida.
 *
 * Cualquier entrada que no describa una franja utilizable cae al default en vez de
 * viajar al motor: `/plan` exige `end > start` y responde `422`, de modo que un campo
 * a medio escribir tumbaría el grupo entero. Perder la corrida por un tipeo es peor
 * que planificar con el default y declararlo en pantalla.
 */
export function resolveShift(
  start: string | null | undefined,
  end: string | null | undefined,
): { start: string; end: string } {
  const from = toMinutes(start);
  const to = toMinutes(end);
  if (from === null || to === null) return { ...DEFAULT_SHIFT };
  if (to - from < MIN_SHIFT_MINUTES) return { ...DEFAULT_SHIFT };
  return { start: start as string, end: end as string };
}
